import type { CustomFieldResource, ICustomField } from "store/customFieldsStore";
import type { TPortalType } from "store/types";
import { EPortalType } from "store/types";

export type AgreementVariableGroupId =
    | "work"
    | "title"
    | "term"
    | "article"
    | "beneficiary"
    | "company"
    | "channel"
    | "other";

export type TemplateVariableEntry = [placeholder: string, label: string];

export type TemplateVariableGroup = {
    id: AgreementVariableGroupId;
    items: TemplateVariableEntry[];
};

const BENEFICIARY_VARIABLE_GROUPS: Record<string, AgreementVariableGroupId> = {
    name: "beneficiary",
    firstName: "beneficiary",
    lastName: "beneficiary",
    firstNameLegalEntity: "beneficiary",
    lastNameLegalEntity: "beneficiary",
    birthDate: "beneficiary",
    address: "beneficiary",
    beneficiarySiret: "beneficiary",
    beneficiaryNIR: "beneficiary",
    beneficiaryEmail: "beneficiary",
    beneficiaryPhone: "beneficiary",
    beneficiaryIBAN: "beneficiary",
    beneficiarySWIFT: "beneficiary",
};

const BOOK_VARIABLE_GROUPS: Record<string, AgreementVariableGroupId> = {
    title: "work",
    workCode: "work",
    associatedBrand: "work",
    ean13: "title",
    articleTitle: "title",
    articleReleaseDate: "title",
    articleFormat: "title",
    duration: "title",
    initialDraw: "title",
    periodicite: "term",
    advance: "term",
    endDate: "term",
    company: "company",
    companyHQ: "company",
    ...BENEFICIARY_VARIABLE_GROUPS,
};

const PRESS_VARIABLE_GROUPS: Record<string, AgreementVariableGroupId> = {
    article: "article",
    publication: "article",
    reference: "article",
    netToPay: "article",
    grossDuty: "article",
    issn: "title",
    siret: "company",
    company: "company",
    ...BENEFICIARY_VARIABLE_GROUPS,
};

export const GROUP_ORDER_BOOK: AgreementVariableGroupId[] = [
    "work",
    "title",
    "term",
    "channel",
    "beneficiary",
    "company",
    "other",
];
export const GROUP_ORDER_PRESS: AgreementVariableGroupId[] = [
    "article",
    "title",
    "term",
    "beneficiary",
    "company",
    "other",
];

const VARIABLE_GROUP_I18N: Record<AgreementVariableGroupId, string> = {
    work: "ContractValidationEdit.VariableGroupWork",
    title: "ContractValidationEdit.VariableGroupTitle",
    term: "ContractValidationEdit.VariableGroupTerm",
    article: "ContractValidationEdit.VariableGroupArticle",
    beneficiary: "ContractValidationEdit.VariableGroupBeneficiary",
    company: "ContractValidationEdit.VariableGroupCompany",
    channel: "ContractValidationEdit.VariableGroupChannel",
    other: "ContractValidationEdit.VariableGroupOther",
};

export const getVariableGroupI18nKey = (groupId: AgreementVariableGroupId, portalType?: TPortalType) => {
    if (groupId === "title" && portalType && portalType !== EPortalType.BOOK) {
        return `ContractValidationEdit.VariableGroupTitle_${portalType}`;
    }
    return VARIABLE_GROUP_I18N[groupId];
};

export const templateVariableKey = (placeholder: string) => placeholder.replace("{{", "").replace("}}", "");

const META_RESOURCE_GROUPS: Record<CustomFieldResource, AgreementVariableGroupId> = {
    title: "title",
    beneficiary: "beneficiary",
    term: "term",
};

export const resolveTemplateVariableGroup = (
    key: string,
    portalType: TPortalType,
    customFieldByUuid: Map<string, ICustomField>,
): AgreementVariableGroupId => {
    if (key === "channel:XX") return "channel";
    if (key.startsWith("channel:") || key.startsWith("channelGroup:")) return "term";
    if (key.startsWith("meta:")) {
        const customField = customFieldByUuid.get(key.slice(5));
        if (!customField) return "other";
        return META_RESOURCE_GROUPS[customField.relatedResource];
    }
    const keyMap = portalType === EPortalType.BOOK ? BOOK_VARIABLE_GROUPS : PRESS_VARIABLE_GROUPS;
    return keyMap[key] ?? "other";
};

export const groupTemplateVariableEntries = (
    entries: TemplateVariableEntry[],
    portalType: TPortalType,
    customFields: ICustomField[],
): TemplateVariableGroup[] => {
    const customFieldByUuid = new Map(customFields.map((field) => [field.uuid, field]));
    const groupsById = new Map<AgreementVariableGroupId, TemplateVariableEntry[]>();
    const order = portalType === EPortalType.BOOK ? GROUP_ORDER_BOOK : GROUP_ORDER_PRESS;

    for (const entry of entries) {
        const groupId = resolveTemplateVariableGroup(templateVariableKey(entry[0]), portalType, customFieldByUuid);
        const groupItems = groupsById.get(groupId) ?? [];
        groupItems.push(entry);
        groupsById.set(groupId, groupItems);
    }

    return order
        .map((groupId) => ({ id: groupId, items: groupsById.get(groupId) ?? [] }))
        .filter((group) => group.items.length > 0);
};
